import { FormProvider, useForm } from "react-hook-form";
import React, { useEffect } from "react";
import { FaLongArrowAltDown, FaSave } from "react-icons/fa";
import { FaLongArrowAltUp } from "react-icons/fa";
import { HiDuplicate } from "react-icons/hi";
import { FaTrashAlt } from "react-icons/fa";
import { yupResolver } from "@hookform/resolvers/yup";
import { IoWarning } from "react-icons/io5";

import { RoundedQuestionBox } from "../RoundedQuestionBox";
import {
  QUESTION_TEXT_FORM_NAMES,
  TextQuestionForm,
  TextQuestionFormValues,
  textQuestionSchema,
} from "./forms/TextQuestionForm";
import {
  QUESTION_RATING_FORM_NAMES,
  RatingQuestionForm,
  RatingQuestionFormValues,
  ratingQuestionSchema,
} from "./forms/RatingQuestionForm";
import {
  QUESTION_SELECT_FORM_NAMES,
  SelectQuestionForm,
  SelectQuestionFormValues,
  selectQuestionSchema,
} from "./forms/SelectQuestionForm";
import { QUESTION_BASE_FORM_NAMES, questionBaseSchema } from "./forms/BaseQuestionForm";
import { Question } from "survey";
import { QuestionOptions } from "../AddQuestion/AddQuestionBody";
import { PaziaLoader } from "components/atoms";
import { useSurvey } from "../store/reducer";

interface QuestionProps {
  question: Question;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** a move is being saved — the arrows wait */
  moving?: boolean;
  /** the editor saves the order on its own right after a move, so a move doesn't make this question unsaved */
  orderSavedSeparately?: boolean;
  onSubmit: (
    data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues
  ) => Promise<number | void>;
}

export const QuestionTile = ({
  question,
  isOpen,
  onSubmit,
  onClose,
  onOpen,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  moving,
  orderSavedSeparately,
}: QuestionProps) => {
  const methods = useForm({
    // @ts-ignore
    resolver: yupResolver(resolveSchema(question)),
    defaultValues: {
      [QUESTION_BASE_FORM_NAMES.ID]: question.id?.toString() || undefined,
      [QUESTION_BASE_FORM_NAMES.INDEX]: question.index,

      [QUESTION_BASE_FORM_NAMES.QUESTION]: question.question,
      [QUESTION_BASE_FORM_NAMES.DESCRIPTION]: question.description,
      [QUESTION_BASE_FORM_NAMES.REQUIRED]: question.required,
      [QUESTION_BASE_FORM_NAMES.INPUT_TYPE]: question.inputType,
      // @ts-ignore
      [QUESTION_SELECT_FORM_NAMES.OPTIONS]: question.options,
      // @ts-ignore
      [QUESTION_TEXT_FORM_NAMES.PLACEHOLDER]: question.placeholder,
      [QUESTION_TEXT_FORM_NAMES.ALLOW_ATTACHMENTS]: question.allowAttachments,
      [QUESTION_TEXT_FORM_NAMES.TEXT_TYPE]: question.textType,
      [QUESTION_RATING_FORM_NAMES.LOWER_LABEL]: question.lowerLabel,
      [QUESTION_RATING_FORM_NAMES.UPPER_LABEL]: question.upperLabel,
    },
  });
  const { dispatch } = useSurvey();

  const {
    formState: { isDirty, isSubmitting },
    reset,
    getValues,
    setValue,
    handleSubmit,
  } = methods;

  const option = QuestionOptions.find((option) => option.type === question.inputType)!;

  const onDuplicateHandler = (event: any) => {
    event?.stopPropagation();
    onDuplicate();
  };

  const onDeleteHandler = (event: any) => {
    event?.stopPropagation();
    onDelete();
  };

  const onSubmitHandler = async (
    data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues
  ) => {
    const id = await onSubmit(data);
    if (id)
      dispatch({
        type: "UPDATE_QUESTION",
        // @ts-ignore
        payload: {
          ...question,
          ...data,
          id,
        },
      });
  };
  const onSaveClick = async (event: React.MouseEvent<SVGElement>) => {
    event?.stopPropagation();
    if (isSubmitting) return;
    await handleSubmit(onSubmitHandler)();
    reset(getValues(), { keepDirty: false });
  };

  const onMoveDownClick = (event: React.MouseEvent<SVGElement>) => {
    event?.stopPropagation();
    onMoveDown();
  };

  const onMoveUpClick = (event: React.MouseEvent<SVGElement>) => {
    event?.stopPropagation();
    onMoveUp();
  };

  useEffect(() => {
    if (question.index !== getValues(QUESTION_BASE_FORM_NAMES.INDEX)) {
      setValue(QUESTION_BASE_FORM_NAMES.INDEX, question.index, { shouldDirty: !orderSavedSeparately });
    }
  }, [question.index]);

  const dirtyOrNew = isDirty || !question.id;

  return (
    <FormProvider {...methods}>
      <div className="relative">
        <RoundedQuestionBox
          startIndex={question.index + 1}
          isOpen={isOpen}
          onClick={() => (isOpen ? onClose() : onOpen())}
        >
          <div>
            <option.icon size={20} className="text-slate-500" />
          </div>
          <div className="grow min-w-0">
            <div className="font-semibold break-words">{question.question}</div>
            {question.required && <div className="text-xs text-gray-400">Wymagane</div>}
          </div>
          <div className="flex gap-2 items-center text-gray-600 dark:text-slate-400">
            <FaLongArrowAltDown
              size={20}
              className={moving ? movingIconClass : iconClass}
              title="Do dołu"
              onClick={onMoveDownClick}
            />
            <FaLongArrowAltUp
              size={20}
              className={moving ? movingIconClass : iconClass}
              title="Do góry"
              onClick={onMoveUpClick}
            />
            <HiDuplicate size={20} onClick={onDuplicateHandler} className={iconClass} title="Duplikuj" />
            <FaTrashAlt size={20} onClick={onDeleteHandler} className={iconClass} title="Usuń" />
            {dirtyOrNew && (
              <FaSave
                size={20}
                className={
                  isSubmitting
                    ? "text-slate-500 cursor-not-allowed"
                    : `hover:text-slate-900 cursor-pointer dark:hover:text-slate-200`
                }
                onClick={onSaveClick}
                title="Zapisz"
              />
            )}
          </div>
        </RoundedQuestionBox>
        <div className={`bg-white justify-left flex ${!isOpen && "hidden"} dark:bg-slate-800`}>
          <form className="pl-[50px] py-4 w-full pr-4 gap-2 flex flex-col">{resolveForm(question)}</form>
        </div>

        {dirtyOrNew && (
          <span className="text-end text-sm  font-semibold absolute top-1 right-2">
            {isSubmitting ? (
              <PaziaLoader size="small" />
            ) : (
              <IoWarning title="Niezapisane zmiany" className="text-yellow-500" />
            )}
          </span>
        )}
      </div>
    </FormProvider>
  );
};

const iconClass = "text-slate-700 hover:text-black dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer";
const movingIconClass = "text-slate-400 dark:text-slate-600 cursor-wait";

const resolveForm = (question: Question) => {
  switch (question.inputType) {
    case "free-text":
      return <TextQuestionForm question={question} />;
    case "single-select":
    case "multi-select":
      return <SelectQuestionForm question={question} />;
    case "rating":
      return <RatingQuestionForm question={question} />;

    case "course-rating":
      return (
        <div className="text-slate-500 text-sm">
          Standardowy formularz oceny kursu w skali gwiazdkowej (1-5), z&nbsp;możliwością zostawienia komentarza.
        </div>
      );
    default:
      return null;
  }
};

const resolveSchema = (question: Question) => {
  switch (question.inputType) {
    case "free-text":
      return textQuestionSchema;
    case "single-select":
    case "multi-select":
      return selectQuestionSchema;
    case "rating":
      return ratingQuestionSchema;
    case "course-rating":
    case "trainer-rating":
      return questionBaseSchema;
    default:
      return null;
  }
};
